import { useDisclosure, UseDisclosureReturn } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  IInvoiceAddressDataSchema,
  IInvoiceRespSchema,
  InvoiceAddressDataSchema,
  InvoiceStatusEnum,
  InvoiceSummaryResp,
  InvoiceTypes,
  ITaxConnectionBaseSchema,
  ProductTypeEnum,
  TaxStatusEnum,
} from '@monetize/types/app';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import {
  doDownloadInvoiceAsCsv,
  doPrintInvoiceToPdf,
  useSetContactOnInvoice,
} from '../../api/accountsService';
import { handleApiErrorToast } from '../../api/axios';
import { useSetAddressOnInvoice } from '../../api/invoiceService';
import {
  useDeleteEntity,
  useGetListData,
  usePerformEntityAction,
} from '../../api/queryUtils';
import { useRevRecSettings } from '../../api/revenueService';
import { ContactFormModal } from '../../components/Contacts/ContactFormModal';
import DataTableActions, {
  ActionType,
} from '../../components/Monetize/DataTable/MDataTableActions';
import {
  INVOICE_EMAIL_STATUSES,
  REVENUE_SCHEDULE_STATUSES,
} from '../../constants/invoice';
import {
  getAccountInvoicesRoute,
  getInvoiceDetailContractsEdit,
  getOneTimeInvoiceCreateRoute,
} from '../../constants/routes';
import { useConfirmModal } from '../../services/confirmModal';
import { useFlags } from '../../services/launchDarkly';
import { logger } from '../../services/logger';
import { downloadAndSaveToPDF } from '../../utils';
import { downloadBlobAsFile } from '../../utils/download';
import {
  getVoidInvoiceDisabledReason,
  isSummaryInvoiceResponse,
} from '../../utils/invoices';
import { getDuplicateInvoiceActionTitle } from '../AccountDetails/OneTimeInvoice/oneTimeInvoice.utils';
import { EditContactDetailModal } from './EditContactDetailModal';
import { EmailInvoiceModal } from './EmailInvoiceModal';
import { InvoiceRevenueScheduleModal } from './InvoiceRevenueSchedule/InvoiceRevenueScheduleModal';
import { VoidInvoiceModal } from './VoidInvoiceModal';

interface InvoiceActionProps {
  invoice: IInvoiceRespSchema | InvoiceSummaryResp;
  isReadOnly?: boolean;
  source?: 'accounts' | 'dashboard';
  refreshData: () => void;
  recalculateTax: () => Promise<IInvoiceRespSchema>;
  onCreateCreditNote: () => void;
  editContactModalState: UseDisclosureReturn;
  onOpenCreditAndRebillModal: () => void;
  onOpenPaymentInstructionsModal: () => void;
}

export const InvoiceActions = ({
  invoice,
  isReadOnly = false,
  source,
  editContactModalState,
  onOpenCreditAndRebillModal,
  onOpenPaymentInstructionsModal,
  refreshData,
  recalculateTax,
  onCreateCreditNote,
}: InvoiceActionProps) => {
  const navigate = useNavigate();
  const { enableUpdatePaymentInstructions, enableInvoiceVoid } = useFlags();
  const { data: recRevSettings } = useRevRecSettings();
  const { mutateAsync: doUpdateInvoiceAddress } = useSetAddressOnInvoice();
  const addressForm = useForm<IInvoiceAddressDataSchema>({
    resolver: zodResolver(InvoiceAddressDataSchema),
    mode: 'onChange',
    defaultValues: {},
  });

  const contactModalState = useDisclosure();
  const revenueScheduleModalState = useDisclosure();
  const emailInvoiceModalState = useDisclosure();
  const voidInvoiceModalState = useDisclosure();
  const { showConfirmModal, setModalLoading, closeModal } = useConfirmModal();

  const { data: isTaxProviderConnected } = useGetListData<
    ITaxConnectionBaseSchema,
    boolean
  >('tax', undefined, {
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 5,
    select: (data) => {
      const taxProvider = data as unknown as ITaxConnectionBaseSchema[];
      return taxProvider?.some(({ status }) => status === TaxStatusEnum.ACTIVE);
    },
  });

  const [contact, setContact] = useState<{
    billingContactId: string;
    shippingContactId: string;
  }>();
  const [isActionFor, setIsActionFor] = useState<'BILLING' | 'SHIPPING'>(
    'BILLING',
  );

  const isCreditAndRebillDisabledForOnetimeInvoice = useMemo(() => {
    return (
      invoice.invoiceType === InvoiceTypes.ONETIME &&
      invoice.invoiceItems.some(
        (item) => item.productType === ProductTypeEnum.USAGE,
      )
    );
  }, [invoice]);

  const { mutateAsync: doSetContactOnInvoice } = useSetContactOnInvoice();
  const { mutateAsync: finalizeInvoiceWithTaxError } =
    usePerformEntityAction<IInvoiceRespSchema>('invoices', {
      onSuccess: () => refreshData(),
    });
  const { mutateAsync: deleteInvoice } = useDeleteEntity('invoices', {
    meta: { showErrorToast: true },
  });

  if (!invoice) {
    return null;
  }

  const handleClickContinue = async ({
    billingContactId,
    shippingContactId,
    action,
    isSubmit,
  }: {
    billingContactId: string;
    shippingContactId: string;
    action?: 'BILLING' | 'SHIPPING';
    isSubmit?: boolean;
  }) => {
    if (isSubmit) {
      await doSetContactOnInvoice({
        invoiceId: invoice?.id,
        data: {
          contactId: billingContactId,
          shippingContactId,
        },
      });

      await doUpdateInvoiceAddress({
        invoiceId: invoice.id,
        data: addressForm.getValues(),
      });
      refreshData();
      editContactModalState.onClose();
    } else {
      setIsActionFor(action!);
      setContact({ billingContactId, shippingContactId });
      contactModalState.onOpen();
    }
  };

  const handleContactModalClose = async () => {
    contactModalState.onClose();
    setContact(undefined);
  };

  async function handleReCalculateTax() {
    showConfirmModal({
      title: 'Recalculate Tax',
      description:
        'Are you sure you want to Recalculate Tax? If you have already sent the invoice to your customer, the amount may change.',
      onYes: async () => {
        try {
          setModalLoading(true);
          await recalculateTax();
        } catch {
          // message to user is already handled in parent component
          logger.warn('Could not Recalculate taxes');
        } finally {
          setModalLoading(false);
          closeModal();
        }
      },
      yesButton: 'Yes',
      yesBtnProps: {
        variant: 'primary',
      },
      noBtnProps: {
        variant: 'cancel',
      },
    });
  }

  async function handleDeleteInvoice() {
    showConfirmModal({
      title: 'Are you sure you want to delete this Invoice?',
      description:
        'Deleting this invoice is irreversible. This action cannot be undone.',
      onYes: async () => {
        try {
          setModalLoading(true);
          await deleteInvoice({ id: invoice.id });
          closeModal();
          navigate(getAccountInvoicesRoute(invoice.account.id));
        } catch {
          setModalLoading(false);
          closeModal();
        }
      },
      yesButton: 'Delete Invoice',
      yesBtnProps: {
        variant: 'delete',
      },
      noBtnProps: {
        variant: 'cancel',
      },
    });
  }

  async function handleFinalizeWithTaxError() {
    showConfirmModal({
      title: 'Finalize with Tax Error',
      description:
        'Are you sure you want to finalize this invoice without tax? This action cannot be undone.',
      onYes: async () => {
        try {
          setModalLoading(true);
          await finalizeInvoiceWithTaxError({
            id: invoice.id,
            action: 'finalize',
            params: { overrideTaxError: 'true' },
          });
        } catch (error) {
          handleApiErrorToast(error);
        } finally {
          setModalLoading(false);
          closeModal();
        }
      },
      yesButton: 'Yes',
      yesBtnProps: {
        variant: 'primary',
      },
      noBtnProps: {
        variant: 'cancel',
      },
    });
  }

  const actions: ActionType[] = [
    {
      title: 'View Contract',
      enabled: !!invoice.contractId,
      action: () => {
        try {
          const contractPath = getInvoiceDetailContractsEdit(
            invoice.contractId,
          );
          navigate(contractPath, { state: { source } });
        } catch (err) {
          handleApiErrorToast(err);
        }
      },
    },
    {
      title: 'Print to PDF',
      enabled: true,
      action: () =>
        downloadAndSaveToPDF(() =>
          doPrintInvoiceToPdf(invoice.id, invoice.invoiceNumber),
        ),
    },
    {
      title: 'Email Invoice',
      enabled:
        !isReadOnly &&
        !isSummaryInvoiceResponse(invoice) &&
        !!invoice.billingContact?.email &&
        INVOICE_EMAIL_STATUSES.has(invoice.status),
      action: () => emailInvoiceModalState.onOpen(),
    },
    {
      title: 'Export CSV',
      enabled: true,
      action: async () => {
        try {
          const resp = await doDownloadInvoiceAsCsv(invoice.id);
          downloadBlobAsFile(
            resp,
            `${invoice.invoiceNumber || invoice.id}.csv`,
            'text/csv;encoding:utf-8',
          );
        } catch (err) {
          handleApiErrorToast(err);
        }
      },
    },
    {
      title: 'View Revenue Schedule',
      enabled:
        recRevSettings?.enabled &&
        REVENUE_SCHEDULE_STATUSES.has(invoice.status),
      action: () => revenueScheduleModalState.onOpen(),
    },
    {
      title: getDuplicateInvoiceActionTitle(invoice.invoiceType),
      enabled: !isReadOnly,
      action: () =>
        navigate(
          getOneTimeInvoiceCreateRoute(invoice.account.id, {
            sourceInvoiceId: invoice.id,
          }),
        ),
    },
    {
      title: 'Finalize with Tax Error',
      enabled:
        !isReadOnly &&
        !isSummaryInvoiceResponse(invoice) &&
        invoice.status === InvoiceStatusEnum.DRAFT &&
        !!invoice.taxError,
      action: handleFinalizeWithTaxError,
    },
    {
      title: 'Recalculate Tax',
      enabled:
        !isReadOnly &&
        !isSummaryInvoiceResponse(invoice) &&
        invoice.status === InvoiceStatusEnum.DRAFT &&
        isTaxProviderConnected,
      action: handleReCalculateTax,
    },
    {
      title: 'Update Payment Instructions',
      enabled:
        enableUpdatePaymentInstructions &&
        invoice.status !== InvoiceStatusEnum.VOID,
      action: onOpenPaymentInstructionsModal,
    },
  ];

  if (invoice.amountCredited < invoice.amount && !isReadOnly) {
    actions.push({
      title: 'Create a Credit Note',
      enabled: invoice.status === InvoiceStatusEnum.UNPAID,
      action: onCreateCreditNote,
    });
  }

  actions.push({
    title: 'Credit and Rebill',
    enabled:
      !isReadOnly &&
      InvoiceStatusEnum.DRAFT !== invoice.status &&
      InvoiceStatusEnum.VOID !== invoice.status &&
      !(invoice as IInvoiceRespSchema).creditRebilledTargetInvoiceId,
    action: onOpenCreditAndRebillModal,
    isDisabled: isCreditAndRebillDisabledForOnetimeInvoice,
    tooltipMsg: isCreditAndRebillDisabledForOnetimeInvoice
      ? 'Credit and Rebill is not available for Onetime Invoices with Usage Products. If you need to re-issue this invoice, manually create a Credit Note and then re-create the invoice.'
      : undefined,
  });

  actions.push({
    title: 'Delete',
    enabled:
      !isReadOnly &&
      !isSummaryInvoiceResponse(invoice) &&
      invoice.status === InvoiceStatusEnum.DRAFT &&
      invoice.invoiceType === InvoiceTypes.ONETIME,
    action: handleDeleteInvoice,
  });

  actions.push({
    title: 'Void Invoice',
    enabled:
      enableInvoiceVoid &&
      !isReadOnly &&
      !isSummaryInvoiceResponse(invoice) &&
      !getVoidInvoiceDisabledReason(invoice),
    action: () => voidInvoiceModalState.onOpen(),
  });

  return (
    <>
      <DataTableActions actions={actions} />

      {editContactModalState.open && !isSummaryInvoiceResponse(invoice) && (
        <EditContactDetailModal
          isOpen
          invoice={invoice!}
          onClose={() => editContactModalState.onClose()}
          onContinue={handleClickContinue}
          addressForm={addressForm}
        />
      )}

      {contactModalState.open && isActionFor && contact && (
        <ContactFormModal
          isOpen
          accountId={invoice?.account.id}
          contactId={
            isActionFor === 'BILLING'
              ? contact?.billingContactId
              : contact?.shippingContactId
          }
          onClose={() => contactModalState.onClose()}
          onUpdatedContact={handleContactModalClose}
        />
      )}

      {revenueScheduleModalState.open && (
        <InvoiceRevenueScheduleModal
          isOpen
          invoice={invoice}
          onClose={() => revenueScheduleModalState.onClose()}
        />
      )}
      {emailInvoiceModalState.open && !isSummaryInvoiceResponse(invoice) && (
        <EmailInvoiceModal
          isOpen
          invoice={invoice}
          onClose={emailInvoiceModalState.onClose}
          refreshData={refreshData}
        />
      )}
      {enableInvoiceVoid &&
        voidInvoiceModalState.open &&
        !isSummaryInvoiceResponse(invoice) && (
          <VoidInvoiceModal
            isOpen
            invoice={invoice}
            onClose={voidInvoiceModalState.onClose}
            refreshData={refreshData}
          />
        )}
    </>
  );
};
